import { useEffect, useRef, useState, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { FaCheck, FaRegCalendar, FaRegCalendarPlus } from 'react-icons/fa';
import { useRouter } from 'next/router';
import Confetti from 'react-confetti';

import { hideModal, showModal } from 'legacy/actions/modalActions';
import { useGetMyMultiTicketsForEvent } from 'legacy/tickets/hooks';
import Button, { ButtonType } from 'legacy/common/components/button/Button';
import { EventForUser } from 'legacy/events/types';
import { useCheckout } from '../../context';
import { useViewEvent } from 'features/events/context/ViewEvent';
import {
  useGetTicketInfoRequired,
  useGetMyEligibleOffers
} from 'legacy/events/hooks';
import AddToCalendar from 'legacy/tickets/components/AddToCalendar';
import { toCalendarEvent } from 'legacy/tickets/helpers';
import GuestSuggestions from './components/GuestSuggestions';
import DownloadApp from './components/DownloadApp';
import Offers from './components/Offers';

import styles from './styles.module.scss';
import { FaShareNodes } from 'react-icons/fa6';
import TicketModalNew from 'legacy/tickets/components/TicketModalNew';
import { useTranslation } from 'react-i18next';
import i18n from 'i18n';

type SuccessViewProps = {
  event: EventForUser;
  price: string;
  isEligibleForOffers?: boolean;
  onClickViewEvent: () => void;
};

const SuccessStep = () => {
  const router = useRouter();

  const { event } = useViewEvent();
  const {
    checkoutSession: { checkoutSession }
  } = useCheckout();

  const { data: offers } = useGetMyEligibleOffers(event._id);

  const isEligibleForOffers = offers?.length > 0;
  const price = (checkoutSession?.total.toFixed(2) || '0') as string;

  const onClickViewEvent = () => {
    router.push(`/events/${event._id}`);
  };

  return (
    <SuccessView
      event={event}
      price={price}
      onClickViewEvent={onClickViewEvent}
      isEligibleForOffers={isEligibleForOffers}
    />
  );
};

const SuccessView: React.FC<SuccessViewProps> = ({
  event,
  price,
  onClickViewEvent,
  isEligibleForOffers
}) => {
  const { t } = useTranslation(['checkout', 'events']);
  const [width, setWidth] = useState(0);
  const [height, setHeight] = useState(0);
  const [numberOfPieces, setNumberOfPieces] = useState(100);
  const [isTicketModalOpen, setIsTicketModalOpen] = useState(false);
  const confettiZoneRef = useRef<HTMLDivElement>(null);

  const { data } = useGetMyMultiTicketsForEvent(event._id);

  const tickets = data?.tickets || [];

  const { data: ticketInfoRequired, refetch: refetchTicketInfoRequired } =
    useGetTicketInfoRequired(event._id, { retry: 5 });

  const calendarEvent = useMemo(
    () =>
      toCalendarEvent({
        title: event?.title,
        startDate: new Date(event?.startDate),
        endDate: new Date(event?.endDate || event?.startDate),
        location: event?.address
      }),
    [event?.title, event?.startDate, event?.endDate, event?.address]
  );

  const updateConfettiDimensions = () => {
    if (confettiZoneRef.current) {
      setHeight(confettiZoneRef.current.clientHeight);
      setWidth(confettiZoneRef.current.clientWidth);

      const isMediumScreen = window.innerWidth >= 768;
      setNumberOfPieces(isMediumScreen ? 250 : 100);
    }
  };

  useEffect(() => {
    updateConfettiDimensions();

    window.addEventListener('resize', updateConfettiDimensions);

    return () => {
      window.removeEventListener('resize', updateConfettiDimensions);
    };
  }, []);

  const dispatch = useDispatch();

  const renderButtons = () => {
    if (!ticketInfoRequired) {
      return (
        <Button
          className="bg-gray-300! text-gray-600! font-medium! tracking-wide max-w-[343px] mx-auto w-[341px] cursor-not-allowed"
          type={ButtonType.Primary}
          disabled
        >
          <div className="flex items-center justify-center">
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-600 mr-2" />
            {t('checkout:processingTicket')}
          </div>
        </Button>
      );
    }

    if (ticketInfoRequired?.ticketNotes.required) {
      return (
        <Button
          className="bg-[#3853C4]! text-[#EFF2FF]! font-medium! tracking-wide max-w-[343px] mx-auto w-[341px]"
          type={ButtonType.Primary}
          onClick={() => {
            dispatch(
              showModal('TICKET_NOTES', {
                event,
                guestQuestions: ticketInfoRequired?.ticketNotes?.guestQuestions,
                onClose: () => {
                  dispatch(hideModal());
                },
                onSuccess: () => {
                  refetchTicketInfoRequired?.();
                  dispatch(hideModal());
                }
              })
            );
          }}
        >
          <div className="flex items-center justify-center">
            {t('checkout:fillOutRequestedInformation')}
          </div>
        </Button>
      );
    }

    if (ticketInfoRequired?.googleForms?.required) {
      return (
        <Button
          className="bg-[#3853C4]! text-[#EFF2FF]! font-medium! tracking-wide max-w-[343px] mx-auto w-[341px]"
          type={ButtonType.Primary}
          onClick={() => {
            dispatch(
              showModal('FILL_FORM', {
                event,
                onSuccess: () => {
                  refetchTicketInfoRequired?.();

                  dispatch(hideModal());
                }
              })
            );
          }}
        >
          <div className="flex items-center justify-center">
            {t('checkout:fillOutRequestedInformation')}
          </div>
        </Button>
      );
    }

    return (
      <div className="flex flex-col md:flex-row gap-4 w-full justify-center items-center">
        {tickets.length > 1 && (
          <Button
            className="bg-[#EFF2FF]! text-[#3853C4]! border-none! font-medium! tracking-wide max-w-[343px] w-[341px]"
            type={ButtonType.Primary}
            onClick={() => {
              setIsTicketModalOpen(true);
            }}
            testId="share-additional-tickets"
          >
            <div className="flex items-center justify-center">
              <FaShareNodes className="mr-2" />
              {t('checkout:shareAdditionalTickets')}
            </div>
          </Button>
        )}
        <Button
          className="bg-[#3853C4]! text-[#EFF2FF]! font-medium! tracking-wide max-w-[343px] w-[341px]"
          type={ButtonType.Primary}
          onClick={() => {
            onClickViewEvent();
          }}
          testId="view-event"
        >
          <div className="flex items-center justify-center">
            <FaRegCalendar className="mr-2" />
            {t('events:actions.viewEvent')}
          </div>
        </Button>
      </div>
    );
  };

  return (
    <div>
      <div
        className="absolute top-0 left-0 w-full h-[200px] md:h-[450px]"
        style={{
          backgroundImage: `url(${event.photo.url})`,
          backgroundSize: 'cover'
        }}
      />
      <div
        className="absolute top-0 left-0 w-full h-[200px] md:h-[450px] backdrop-blur-[20px]"
        style={{
          background:
            'linear-gradient(180deg, rgba(255, 255, 255, 0) 0%, rgba(237, 237, 237, 0.4) 50.18%, #fff 100%)'
        }}
      />

      <div
        className="absolute top-0 left-0 w-full h-[200px] md:h-[450px] pointer-events-none"
        id="confetti-zone"
        ref={confettiZoneRef}
        style={{
          maskImage:
            'linear-gradient(to bottom, black 20%, rgba(0, 0, 0, 0.7) 50%, rgba(0, 0, 0, 0.3) 80%, transparent)',
          WebkitMaskImage:
            'linear-gradient(to bottom, black 20%, rgba(0, 0, 0, 0.7) 50%, rgba(0, 0, 0, 0.3) 80%, transparent)'
        }}
      >
        <Confetti
          width={width}
          height={height}
          numberOfPieces={numberOfPieces}
          colors={['#643DC4', '#F98700', '#7994f7']}
          drawShape={(ctx) => {
            ctx.beginPath();
            ctx.arc(0.2, 0.2, 4, 0, 2 * Math.PI);
            ctx.fill();

            ctx.closePath();
          }}
        />
      </div>
      <div
        className="h-full relative flex flex-col items-center justify-start pb-40 -mt-6 md:mt-20"
        data-testid="view-checkout-success"
      >
        <div className="w-full flex flex-col items-center md:p-10 pb-10 p-4">
          <div className={styles.scaleIn}>
            <div className="flex items-center justify-center">
              <div className={styles.eventPhoto}>
                <img
                  className="w-24 h-24 object-cover rounded-full shadow-xl"
                  src={event.photo.url}
                  alt={event.title}
                />
              </div>

              <div className={styles.checkMark}>
                <div className="w-24 h-24 object-cover rounded-full bg-primary-400 flex items-center justify-center shadow-xl">
                  <FaCheck size={40} color="white" />
                </div>
              </div>
            </div>
          </div>

          <h1 className="mt-10 text-2xl font-bold">
            {t('checkout:youAreAllSet')}
          </h1>
          <p className="mt-4 text-lg text-gray-600 font-normal text-center max-w-[283px] md:max-w-none">
            {t('checkout:youHaveReservedYourTicketsTo', {
              eventTitle: event.title
            })}
          </p>

          {tickets.length > 1 && (
            <div className="mt-2 flex flex-col items-center w-[283px]">
              <p className="text-sm text-gray-600 text-center font-normal">
                {t('checkout:shareAdditionalTicketsDescription')}
              </p>
            </div>
          )}

          <div className="mt-6 w-full flex md:flex-row flex-col md:gap-16 gap-4 mx-auto md:justify-center md:items-center">
            <div className="mt-4 min-w-[200px] flex flex-col gap-1">
              <p className="text-sm text-gray-600">{t('checkout:date')}</p>
              <div className="flex flex-row justify-between md:flex-col gap-1">
                <p className="text-base font-semibold">
                  📅{' '}
                  {new Date(event.startDate).toLocaleDateString(i18n.language, {
                    month: 'long',
                    day: 'numeric',
                    year: 'numeric'
                  })}
                  {' | '}
                  {new Date(event.startDate).toLocaleTimeString(i18n.language, {
                    hour: '2-digit',
                    minute: '2-digit',
                    hour12: true
                  })}
                </p>
                <AddToCalendar
                  position="left"
                  label={
                    <div className="flex items-center gap-1 text-[#3853C4]">
                      <FaRegCalendarPlus
                        size={14}
                        className="cursor-pointer -mt-px"
                      />
                      <span className="text-sm">
                        {t('checkout:addToCalendar')}
                      </span>
                    </div>
                  }
                  event={calendarEvent}
                />
              </div>
            </div>

            <div className="mt-3 min-w-[200px] flex flex-col gap-1">
              <p className="text-sm text-gray-600">{t('checkout:location')}</p>
              <p className="text-base font-semibold">📍{event?.address}</p>
            </div>

            <div className="mt-3 min-w-[200px] flex flex-col gap-1">
              <p className="text-sm text-gray-600">{t('checkout:totalCost')}</p>
              <p className="text-base font-semibold">💰 ${price}</p>
            </div>
          </div>
        </div>

        <div className="flex flex-col flex-1 md:px-16 md:mt-4 px-8 w-full justify-between" />

        <div className="flex flex-col md:flex-row-reverse gap-12 md:gap-10 items-center justify-center md:items-start w-full">
          <div className="flex flex-col items-center gap-0 px-4 md:px-0 w-full md:max-w-[350px]">
            <Offers isEligibleForOffers={isEligibleForOffers} />
            <DownloadApp />
          </div>
          <GuestSuggestions event={event} />
        </div>
      </div>

      <div className="fixed bottom-0 left-0 right-0 p-4 bg-white z-10 border-t border-[#EBEBEB]">
        <div className="w-full flex flex-col gap-4 justify-center items-center">
          {renderButtons()}
        </div>
      </div>

      {isTicketModalOpen && (
        <TicketModalNew
          eventId={event?._id}
          onClose={() => setIsTicketModalOpen(false)}
        />
      )}
    </div>
  );
};

export default SuccessStep;
